import { useContext, useEffect, useState } from 'react';

import { Button } from '@selina-finance/ui';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import { ApplicationStage } from '@src/app/interfaces/application-stage.enum';
import { validateIncomings } from '@src/app/utils/application';
import { containsAnySubStr } from '@src/shared/helpers/string';
import CheckEffectIcon from '@src/ui/components/Icons/CheckEffectIcon';
import EasyIcon from '@src/ui/components/Icons/EasyIcon';

const DashboardView = () => {
    const [isRejected, setIsRejected] = useState<boolean>(false);
    const navigate = useNavigate();

    const {
        dashboardItemStatuses,
        applicationStage,
        debtConUser,
        incomings,
        secondApplicantDeferred,
    } = useContext(ApplicationContext);

    const isDeferredCoApplicantPending =
        secondApplicantDeferred && !validateIncomings(incomings?.[1]);

    useEffect(
        () =>
            setIsRejected(
                containsAnySubStr(applicationStage || '', [
                    ApplicationStage.DECISIONING_DECLINE,
                    ApplicationStage.CLOSED,
                ]),
            ),
        [applicationStage],
    );

    const handleNavigate = () => {
        const isAfterDecisioning =
            applicationStage === ApplicationStage.DECISIONING_DECLINE ||
            applicationStage === ApplicationStage.CLOSED ||
            applicationStage === ApplicationStage.DECISIONING_ACCEPT ||
            applicationStage === ApplicationStage.DECISIONING_ERROR;

        // navigate to applicants2 if second applicant's details were deferred
        // and are still missing, regardless of which Kameleoon variant the
        // user is currently bucketed into.
        // isAfterDecisioning guards this so it only fires before the application has gone through decisioning —
        // once decisioning has run, the second applicant's details can no
        // longer be collected this way.
        // TODO: remove once most of the deferred customers have finished their application.

        if (
            isDeferredCoApplicantPending &&
            dashboardItemStatuses[4].completed &&
            !isAfterDecisioning
        ) {
            navigate('/eligibility/applicants2');
            return;
        }

        const latestStep = dashboardItemStatuses.find(
            (item: { completed: boolean }) => !item.completed,
        );
        if (latestStep === 'debt-con' && debtConUser) {
            navigate('/eligibility/debt-con/intro');
        } else if (latestStep) {
            latestStep.name === 'quote' || latestStep.name === 'debt-con'
                ? navigate('/eligibility/create-account')
                : navigate(`/eligibility/${latestStep.name}`);
        } else {
            const url = new URL(window.location.href);
            const inEligibility = url.pathname.includes('eligibility');
            isRejected &&
                navigate(inEligibility ? './reject' : './eligibility/reject');
        }
    };

    return (
        <div className='grid gap-7'>
            <div className='flex justify-between flex-col sm:flex-row'>
                <div className='w-full lg:w-1/3 sm:pr-4 md:pr-4 lg:pr-0'>
                    <div className='mt-15 flex justify-center lg:hidden'>
                        <EasyIcon width={269} height={269} />
                    </div>
                    <p className='mb-3 mt-0 lg:mt-4 text-2xl lg:text-28 font-sora-bold tracking-tighter'>
                        Complete your application
                    </p>
                    <p className='mb-6 text-body leading-6 text-midnight-200'>
                        You're almost there - to tailor your quote, we need some
                        more details from you.
                    </p>
                    <div className='flex justify-between flex-col leading-6'>
                        <div className='bg-white flex mb-2 p-3 rounded-full'>
                            <div className='mr-2 flex items-center'>
                                <CheckEffectIcon />
                            </div>
                            <p className='text-left text-sm_body leading-4'>
                                Complete your online application{' '}
                                <span className='font-bold'>
                                    in under 5 minutes
                                </span>
                            </p>
                        </div>
                        <div className='bg-white flex flex mb-2 p-3 rounded-full'>
                            <div className='mr-2 flex items-center'>
                                <CheckEffectIcon />
                            </div>
                            <p className='text-left text-sm_body'>
                                Speak to a friendly qualified mortgage advisor
                            </p>
                        </div>
                        <div className='bg-white flex mb-0 lg:mb-4 p-3 rounded-full'>
                            <div className='mr-2 flex items-center'>
                                <CheckEffectIcon />
                            </div>
                            <div className='text-left text-sm_body'>
                                Get your funds in
                                <span className='font-bold ml-1'>
                                    as little as 2 days
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className='hidden lg:flex lg:flex-row lg:justify-end'>
                        <Button
                            className={'mt-2 w-full'}
                            size='large'
                            color='secondary'
                            onClick={() => handleNavigate()}
                        >
                            {dashboardItemStatuses[7].completed
                                ? 'View my quote'
                                : 'Continue application'}
                        </Button>
                    </div>
                    <div className='block lg:hidden flex justify-center mt-4'>
                        <Button
                            className={'w-full'}
                            size='large'
                            color='secondary'
                            onClick={() => handleNavigate()}
                        >
                            {dashboardItemStatuses[7].completed
                                ? 'View my quote'
                                : 'Continue'}
                        </Button>
                    </div>
                </div>
                <div className='hidden lg:flex mr-10'>
                    <EasyIcon width={500} height={350} />
                </div>
            </div>
        </div>
    );
};

export default DashboardView;
